import {
  PAYER_CLASS_OPTIONS,
  serviceLineLabel,
} from "pages/HospitalsPage/components/BenefitSelection/constants";
import {
  BenefitFallbackSource,
  IBorrowingPair,
  IBorrowingScope,
} from "types/BenefitFallbacks/BenefitFallbacks";

/**
 * CLE-3047 — the words Eligibility › Fallbacks’ "How fallbacks work" section uses. Pure, so the
 * sentences are tested without rendering the page. Service lines are named the way the benefit
 * selection screen names them (`serviceLineLabel`). `sourceLabel` is also what the Fallbacks checks list
 * names a donor with (`fallbackCasesWording.ts`), so the rules section and the checks list say the same thing.
 */

/** A source as a service line name. `MEDICARE` is a forced value, never a configured line. */
export const sourceLabel = (source: BenefitFallbackSource): string =>
  source === "MEDICARE"
    ? "Medicare"
    : serviceLineLabel(source as Parameters<typeof serviceLineLabel>[0]);

/** "a", "a and b", "a, b and c". */
export const listInWords = (items: string[]): string =>
  items.length <= 1
    ? items.join("")
    : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

const payerClassLabel = (payerClass: string): string =>
  PAYER_CLASS_OPTIONS.find((option) => option.value === payerClass)?.name ??
  payerClass;

/** True when a scope limits nothing: every payer class, every payer, every plan. */
export const isEveryone = (scope: IBorrowingScope): boolean =>
  scope.payerClass === null &&
  scope.payers.length === 0 &&
  scope.planKeywords.length === 0;

/**
 * Who a scope covers, e.g. "UHC Medicare Advantage" or "Aetna · plans containing “CHOICE”". The
 * service scope is left out: the service line the pair reads already says it.
 */
export const scopeInWords = (scope: IBorrowingScope): string => {
  const parts: string[] = [];
  if (scope.payerClass) parts.push(payerClassLabel(scope.payerClass));
  if (scope.payers.length > 0)
    parts.push(scope.payers.map((payer) => payer.name).join(", "));
  if (scope.planKeywords.length > 0)
    parts.push(
      `plans containing ${scope.planKeywords
        .map((keyword) => `“${keyword}”`)
        .join(" or ")}`
    );
  return parts.length > 0 ? parts.join(" · ") : "everyone";
};

const hospitals = (count: number): string =>
  `${count} hospital${count === 1 ? "" : "s"}`;

/**
 * Who a pair is limited to, for the small line under its service line, e.g. "Only for UHC Medicare
 * Advantage". Null when some hospital declares the pair for everyone.
 */
export const pairLimitInWords = (pair: IBorrowingPair): string | null =>
  pair.scopes.some(isEveryone)
    ? null
    : `Only for ${pair.scopes.map(scopeInWords).join("; ")}.`;

/** The scopes where the same service line borrows nothing, or null when there are none. */
export const exceptionsInWords = (pair: IBorrowingPair): string | null =>
  pair.exceptions.length === 0
    ? null
    : `Not substituted for ${pair.exceptions
        .map(
          (scope) => `${scopeInWords(scope)} (${hospitals(scope.accountCount)})`
        )
        .join("; ")}.`;

/** "Diagnostic Medical, Hospital inpatient and Surgical co-insurance is never substituted." */
export const neverBorrowsInWords = (
  sources: BenefitFallbackSource[]
): string | null =>
  sources.length === 0
    ? null
    : `${listInWords(
        sources.map(sourceLabel)
      )} co-insurance is never substituted.`;

/** The engine's non-answer line kinds, quoted: "“A”, “B” or “C”". */
export const nonAnswerKindsInWords = (kinds: string[]): string => {
  const quoted = kinds.map((kind) => `“${kind}”`);
  return quoted.length <= 1
    ? quoted.join("")
    : `${quoted.slice(0, -1).join(", ")} or ${quoted[quoted.length - 1]}`;
};
